import React, { useEffect, useState } from "react";
import { Search, Download, Filter, AlertCircle } from "lucide-react";

interface Product {
    id: number;
    name: string;
    category: string;
    brand_name: string | null;
    ean_code: string | null;
}

interface Offer {
    id: number;
    currency: string;
    min_quantity: number;
    x: number;
    y: number;
    width: number | null;
    height: number | null;
    packaging: {
        id: number;
        name: string;
        size_value: string | null;
        size_unit: string | null;
        multiplier: number;
    } | null;
}

interface Slice {
    id: number;
    slice_number: number;
    slice_url: string | null;
}

interface Image {
    id: number;
    library_id: number;
}

interface MappedProduct {
    offer_id: number;
    created_at: string;
    product: Product;
    offer: Offer;
    slice: Slice | null;
    image: Image | null;
    attributes: any[];
}

const Angebot: React.FC = () => {
    const [products, setProducts] = useState<MappedProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [categories, setCategories] = useState<string[]>([]);

    useEffect(() => {
        fetchAllMappedProducts();
    }, []);

    const fetchAllMappedProducts = async () => {
        setLoading(true);
        try {
            const res = await fetch("/api/admin/product_identifiers/all_mapped");
            const data = await res.json();

            if (data.success) {
                setProducts(data.products || []);

                // Extract unique categories
                const uniqueCategories = [
                    ...new Set(data.products?.map((p: MappedProduct) => p.product.category).filter(Boolean)),
                ] as string[];
                setCategories(uniqueCategories.sort());
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = products.filter((item) => {
        const matchesSearch =
            item.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.product.brand_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.product.ean_code?.includes(searchTerm);

        const matchesCategory = !filterCategory || item.product.category === filterCategory;

        return matchesSearch && matchesCategory;
    });

    const downloadAsCSV = () => {
        if (filteredProducts.length === 0) return;

        const headers = [
            "Termék ID",
            "Termék név",
            "Kategória",
            "Márka",
            "EAN kód",
            "Ár",
            "Deviza",
            "Min. mennyiség",
            "Kiszerelés",
            "Slice ID",
            "Létrehozva",
        ];

        const rows = filteredProducts.map((item) => [
            item.product.id,
            item.product.name,
            item.product.category,
            item.product.brand_name || "",
            item.product.ean_code || "",
            "", // Price field - leave empty since we moved to image_offer_prices
            item.offer.currency,
            item.offer.min_quantity,
            item.offer.packaging?.name || "",
            item.slice?.id || "",
            new Date(item.created_at).toLocaleString("hu-HU"),
        ]);

        const csvContent = [
            headers.join(";"),
            ...rows.map((row) => row.map((cell) => `"${cell}"`).join(";")),
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `angebote_${new Date().toISOString().split("T")[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-8 overflow-auto">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">
                        Leképezett Ajánlatok
                    </h1>
                    <p className="text-slate-600 dark:text-slate-300">
                        Összes leképezett termék a rendszerben
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                        <div className="text-slate-600 dark:text-slate-400 text-sm font-medium">Összes ajánlat</div>
                        <div className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                            {products.length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                        <div className="text-slate-600 dark:text-slate-400 text-sm font-medium">Szűrt ajánlatok</div>
                        <div className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                            {filteredProducts.length}
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700">
                        <div className="text-slate-600 dark:text-slate-400 text-sm font-medium">Kategóriák</div>
                        <div className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                            {categories.length}
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border border-slate-200 dark:border-slate-700 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                <Search className="inline mr-2" size={16} />
                                Keresés
                            </label>
                            <input
                                type="text"
                                placeholder="Termék név, márka, EAN..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                <Filter className="inline mr-2" size={16} />
                                Kategória
                            </label>
                            <select
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Összes kategória</option>
                                {categories.map((cat) => (
                                    <option key={cat} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-end">
                            <button
                                onClick={downloadAsCSV}
                                disabled={filteredProducts.length === 0}
                                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                            >
                                <Download size={16} />
                                CSV letöltés
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin">
                            <div className="text-slate-600 dark:text-slate-400">Betöltés...</div>
                        </div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-12 border border-slate-200 dark:border-slate-700 text-center">
                        <AlertCircle className="mx-auto text-slate-400 mb-4" size={48} />
                        <p className="text-slate-600 dark:text-slate-400">Nem találhatóak olyan ajánlatok, amelyek megfelelnek a szűrőkritériumoknak.</p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Termék
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Márka
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Kategória
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Ár
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Kiszerelés
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Slice
                                        </th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900 dark:text-white">
                                            Létrehozva
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map((item, index) => (
                                        <tr
                                            key={item.offer_id}
                                            className={
                                                index % 2 === 0
                                                    ? "border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                                                    : "border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-750"
                                            }
                                        >
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-slate-900 dark:text-white">{item.product.name}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">{item.product.ean_code || "—"}</div>
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.product.brand_name || "—"}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.product.category || "—"}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-slate-900 dark:text-white">
                                                    {item.offer.currency}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                {item.offer.packaging?.name || "—"}
                                            </td>
                                            <td className="px-6 py-4">
                                                {item.slice ? (
                                                    <a
                                                        href={item.slice.slice_url || "#"}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                                                    >
                                                        Slice #{item.slice.slice_number}
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-500 dark:text-slate-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-slate-700 dark:text-slate-300 text-sm">
                                                {new Date(item.created_at).toLocaleDateString("hu-HU")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Angebot;
